import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Prompt, useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import BottomButtonGroup, { BUTTON_LOADING_ICON, FORM_IMAGE_CLEAR_BUTTON_SX as IMAGE_CLEAR_BUTTON_SX, FORM_INLINE_ACTION_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import { buildListSearch as buildListSearchQuery, createInitialListState } from '../../utils/listPageState'
import {
    MEDAL_PRODUCT_IMAGE_LEAVE_MESSAGE,
    hasPendingMedalProductImages,
    usePendingMedalProductImageUnloadGuard,
} from './medalProductLeaveGuard'

const EDIT_DRAFT_FIELDS = [
    { field: 'title', param: 'draftTitle' },
    { field: 'minOrderQuantity', param: 'draftMinOrderQuantity' },
    { field: 'quantityDiscount', param: 'draftQuantityDiscount' },
    { field: 'moldPrice', param: 'draftMoldPrice' },
    { field: 'designPrice', param: 'draftDesignPrice' },
    { field: 'packingPrice', param: 'draftPackingPrice' },
    { field: 'info1', param: 'draftInfo1' },
    { field: 'info2', param: 'draftInfo2' },
    { field: 'info3', param: 'draftInfo3' },
    { field: 'info4', param: 'draftInfo4' },
    { field: 'info5', param: 'draftInfo5' },
]

const MEDAL_PRODUCT_IMAGE_FILE_ACCEPT = 'image/*'
const MEDAL_PRODUCT_IMAGE_FILE_ERROR_MESSAGE = '이미지 파일만 업로드할 수 있습니다.'

// 메달 상품 이미지는 브라우저가 image/*로 분류한 파일만 통과시킨다.
const isMedalProductImageFile = (file) => {
    return Boolean(file?.type) && file.type.startsWith('image/')
}

const IMAGE_PANEL_SX = {
    height: '100%',
    p: 2,
    borderRadius: '4px',
    borderColor: 'rgba(148, 163, 184, 0.24)',
    backgroundColor: '#fff',
}

const IMAGE_PREVIEW_FRAME_SX = {
    height: 320,
    borderRadius: '4px',
    border: '1px solid rgba(148, 163, 184, 0.24)',
    backgroundColor: '#f8fafc',
    overflow: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    maxHeight: 500,
    objectFit: 'contain',
    display: 'block',
}

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

// 메달 상품 이미지를 미리보기, 업로드, 선택 취소까지 한 패널 안에서 관리한다.
function MedalProductImagePanel({
    inputId,
    fieldName,
    label,
    helperText,
    imageSrc,
    imageFile,
    editMode,
    disabled,
    onUpload,
    onClear,
}) {
    const fileInputRef = useRef(null)
    const imageFileName = getImageFileName(imageFile)
    const hasPendingFile = imageFile instanceof File

    const handleClearClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }

        onClear(fieldName)
    }

    return (
        <Paper variant="outlined" sx={IMAGE_PANEL_SX}>
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {helperText}
                    </Typography>
                </Box>
                {hasPendingFile && (
                    <Button
                        size="small"
                        color="error"
                        variant="text"
                        startIcon={<RemoveIcon />}
                        onClick={handleClearClick}
                        sx={IMAGE_CLEAR_BUTTON_SX}
                        disabled={disabled}
                    >
                        선택 취소
                    </Button>
                )}
            </Box>
            <Box
                sx={{
                    ...IMAGE_PREVIEW_FRAME_SX,
                    mb: editMode ? 1.5 : 0,
                }}
            >
                <Box
                    component="img"
                    src={imageSrc || '/images/noimage.jpg'}
                    alt={`${label} 미리보기`}
                    sx={IMAGE_PREVIEW_SX}
                />
            </Box>
            {editMode && (
                <>
                    <FormControl fullWidth>
                        <TextField
                            inputProps={{ accept: MEDAL_PRODUCT_IMAGE_FILE_ACCEPT }}
                            inputRef={fileInputRef}
                            style={{ display: 'none' }}
                            name={fieldName}
                            id={inputId}
                            type="file"
                            disabled={disabled}
                            onChange={onUpload}
                        />
                        <Box component="label" htmlFor={inputId} sx={{ display: 'block' }}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                component="span"
                                startIcon={<Add />}
                                sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                                disabled={disabled}
                            >
                                이미지 선택
                            </Button>
                        </Box>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
                    </Typography>
                </>
            )}
        </Paper>
    )
}

export default function MedalProducts({ role }) {
    const location = useLocation()
    const history = useHistory()
    const initialDetailStateRef = useRef(createInitialListState({
        location,
        getExtraState: ({ locationState, searchParams }) => ({
            editMode: searchParams.get('edit') === '1' || Boolean(locationState.editMode),
            draftValues: EDIT_DRAFT_FIELDS.reduce((nextDrafts, { field, param }) => {
                nextDrafts[field] = searchParams.has(param)
                    ? (searchParams.get(param) || '')
                    : (locationState[field] ?? null)
                return nextDrafts
            }, {}),
        }),
    }))
    const initialDetailState = initialDetailStateRef.current
    const detailHydratedRef = useRef(false)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [params, setParams] = useState({})
    const [thumbnail, setThumbnail] = useState('')
    const [detailImage, setDetailImage] = useState('')
    const [editMode, setEditMode] = useState(initialDetailState.editMode)
    const [tempParams, setTempParams] = useState({})
    const [tempThumbnail, setTempThumbnail] = useState('')
    const [tempDetailImage, setTempDetailImage] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(initialDetailState.snackbar.openSnackbar || false)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialDetailState.snackbar.severitySnackbar || 'info')
    const [messageSnackbar, setMessageSnackbar] = useState(initialDetailState.snackbar.messageSnackbar || '')
    const leaveGuardEnabled = editMode && hasPendingMedalProductImages(params) && !saving

    usePendingMedalProductImageUnloadGuard(leaveGuardEnabled)
    
    const resolveImage = useCallback((image) => {
        if (!image) {
            return '/images/noimage.jpg'
        }

        if (typeof image === 'string' && (image.startsWith('data:') || image.startsWith('blob:') || image.startsWith('http') || image.startsWith('/images/'))) {
            return image
        }

        return `${process.env.REACT_APP_AWS_S3_URL}/${image}`
    }, [])

    // 상세 조회 실패 시 편집 상태와 이미지 프리뷰를 한 번에 비워 이전 데이터 노출을 막는다.
    const resetDetailState = useCallback((errorMessage = '') => {
        setParams({})
        setThumbnail('')
        setDetailImage('')
        setTempParams({})
        setTempThumbnail('')
        setTempDetailImage('')
        setEditMode(false)
        setHasDetail(false)
        setDetailErrorMessage(errorMessage)
    }, [])

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
            setHasDetail(false)
        }
        setDetailErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/medal/getProduct', { })
            if (data.code === 200) {
                const detail = data.data || {}
                const hasMedalProduct = Boolean(detail?.medalProductId)

                if (!hasMedalProduct) {
                    resetDetailState('메달 상품 정보를 찾을 수 없습니다.')
                    return
                }

                setParams(detail)
                setThumbnail(resolveImage(detail.thumbnail))
                setDetailImage(resolveImage(detail.detailImage))
                setHasDetail(hasMedalProduct)
                setDetailErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '메달 상품 정보를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '메달 상품 정보를 불러오지 못했습니다.'
            resetDetailState(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [history, resetDetailState, resolveImage])

    useEffect(() => {
        getData()
    }, [getData])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 메달 상품은 단일 설정 화면이라 오류 상태에서도 다시 조회 액션을 제공한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="메달 상품 정보를 불러오지 못했습니다."
            description={detailErrorMessage || '잠시 후 다시 시도해주세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => getData()} backLabel="다시 시도" actionColor="primary" />
            )}
            sx={{ mt: 2, mb: 2 }}
        />
    )

    // 편집 모드는 query로 남겨 새로고침 후에도 같은 작업 상태와 draft를 유지한다.
    const syncEditModeSearch = useCallback((nextEditMode, nextParams = params, nextHasDetail = hasDetail) => {
        const nextSearch = buildListSearchQuery({
            curPage: 1,
            searchQuery: '',
            extraParams: [
                {
                    param: 'edit',
                    value: nextEditMode ? '1' : '',
                    shouldInclude: (value) => value === '1',
                },
                ...EDIT_DRAFT_FIELDS.map(({ field, param }) => ({
                    param,
                    value: nextParams?.[field] ?? '',
                    shouldInclude: () => nextEditMode && nextHasDetail,
                })),
            ],
        })

        if (location.search === nextSearch) {
            return
        }

        history.replace({
            pathname: location.pathname,
            search: nextSearch,
            ...(location.state ? { state: location.state } : {}),
        })
    }, [hasDetail, history, location.pathname, location.search, location.state, params])

    useEffect(() => {
        if (loading) {
            return
        }

        syncEditModeSearch(editMode, params, hasDetail)
    }, [editMode, hasDetail, loading, params, syncEditModeSearch])

    const handleUploadClick = (e, type) => {
        if (saving) {
            return
        }

        e.preventDefault()
        
        let reader = new FileReader()
        let file = e.target.files[0]

        if (!file) {
            return
        }

        if (!isMedalProductImageFile(file)) {
            e.target.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar(MEDAL_PRODUCT_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        reader.onloadend = () => {
            setParams((prevParams) => ({
                ...prevParams,
                [e.target.name]: file
            }))
            if (type === 1) {
                setThumbnail(reader.result)
            } else if (type === 2) {
                setDetailImage(reader.result)
            }
        }
    
        reader.readAsDataURL(file)
    }

    const handleClearImage = (fieldName) => {
        setParams((prevParams) => ({
            ...prevParams,
            [fieldName]: tempParams[fieldName],
        }))

        if (fieldName === 'thumbnail') {
            setThumbnail(tempThumbnail)
        } else if (fieldName === 'detailImage') {
            setDetailImage(tempDetailImage)
        }
    }

    const handleUpdateField = (e) => {
        setParams({
            ...params,
            [e.target.name]: e.target.value
        })
    }

    const handleEditClick = () => {
        if (saving || !hasDetail) {
            return
        }
        setTempParams({ ...params }) // 현재 데이터 백업
        setTempThumbnail(thumbnail)
        setTempDetailImage(detailImage)
        setEditMode(true)
    }

    const handleCancelClick = (event) => {
        if (saving) {
            return
        }

        if (leaveGuardEnabled) {
            event?.preventDefault()

            if (!window.confirm(MEDAL_PRODUCT_IMAGE_LEAVE_MESSAGE)) {
                return
            }
        }

        setParams({ ...tempParams }) // 원래 데이터로 복원
        setThumbnail(tempThumbnail)
        setDetailImage(tempDetailImage)
        setEditMode(false)
    }

    // 새로고침으로 복귀했을 때 서버 원본을 백업한 뒤 draft 값을 현재 편집본에 다시 덮어쓴다.
    useEffect(() => {
        if (loading || !hasDetail || detailHydratedRef.current || !initialDetailState.editMode) {
            return
        }

        detailHydratedRef.current = true
        setTempParams({ ...params })
        setTempThumbnail(thumbnail)
        setTempDetailImage(detailImage)

        const nextParams = { ...params }
        EDIT_DRAFT_FIELDS.forEach(({ field }) => {
            if (initialDetailState.draftValues?.[field] !== null && initialDetailState.draftValues?.[field] !== undefined) {
                nextParams[field] = initialDetailState.draftValues[field]
            }
        })

        setParams(nextParams)
        setEditMode(true)
    }, [hasDetail, initialDetailState.draftValues, initialDetailState.editMode, loading, params, thumbnail, detailImage])

    const updateData = useCallback(async (params) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/medal/updateProduct',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })
            
            if (data.code === 200) {
                await getData(false)
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(data.message)
                setEditMode(false)
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('메달 상품을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }, [getData, history])

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextTitle = (params.title || '').trim()

        if (!nextTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('제목을 입력해주세요.')
            return
        }

        const formData = new FormData()
        formData.append('medalProductId', params.medalProductId)
        formData.append('thumbnail', params.thumbnail)
        formData.append('detailImage', params.detailImage)
        formData.append('title', nextTitle)
        formData.append('minOrderQuantity', params.minOrderQuantity)
        formData.append('quantityDiscount', params.quantityDiscount)
        formData.append('moldPrice', params.moldPrice)
        formData.append('designPrice', params.designPrice)
        formData.append('packingPrice', params.packingPrice)
        formData.append('info1', params.info1)
        formData.append('info2', params.info2)
        formData.append('info3', params.info3)
        formData.append('info4', params.info4)
        formData.append('info5', params.info5)
        
        updateData(formData)
    }

    return (
        <Grid item xs={12}>
            <Prompt when={leaveGuardEnabled} message={MEDAL_PRODUCT_IMAGE_LEAVE_MESSAGE} />
            {loading ? (
                <Spiner/>
            ) : !hasDetail ? (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>메달 정보</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>{editMode ? '메달 수정' : '메달 정보' } </Title>
                    <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                        <Grid container spacing={2} sx={{ marginTop: 2 }}>
                            <Grid item xs={12} md={6}>
                                <MedalProductImagePanel
                                    inputId="thumbnail"
                                    fieldName="thumbnail"
                                    label="메인 이미지"
                                    helperText="상품 목록과 상세 상단에 노출되는 대표 이미지를 확인합니다."
                                    imageSrc={thumbnail}
                                    imageFile={params.thumbnail}
                                    editMode={editMode}
                                    disabled={saving}
                                    onUpload={(e) => handleUploadClick(e, 1)}
                                    onClear={handleClearImage}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <MedalProductImagePanel
                                    inputId="detailImage"
                                    fieldName="detailImage"
                                    label="상세 이미지"
                                    helperText="상품 상세 설명 영역에 노출되는 이미지를 확인합니다."
                                    imageSrc={detailImage}
                                    imageFile={params.detailImage}
                                    editMode={editMode}
                                    disabled={saving}
                                    onUpload={(e) => handleUploadClick(e, 2)}
                                    onClear={handleClearImage}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        InputProps={{ readOnly: !editMode || saving }}
                                        disabled={saving}
                                        label="제목"
                                        required
                                        id="title"
                                        name="title"
                                        value={params.title}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={6} md={6}>
                                <FormControl fullWidth>
                                    <TextField
                                        InputProps={{ readOnly: !editMode || saving }}
                                        disabled={saving}
                                        required
                                        type="text"
                                        label="최소 주문 수량"
                                        id="minOrderQuantity"
                                        name="minOrderQuantity"
                                        value={params.minOrderQuantity}
                                        onChange={(e) => {
                                            let onlyNums = e.target.value.replace(/[^0-9]/g, '')
                                            if (Number(onlyNums) > 999999999) { onlyNums = '999999999' }
                                            handleUpdateField({ target: { name: 'minOrderQuantity', value: onlyNums } })
                                        }}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} sm={6} md={6}>
                                <FormControl fullWidth>
                                    <TextField
                                        InputProps={{ readOnly: !editMode || saving }}
                                        disabled={saving}
                                        required
                                        type="text"
                                        label="1000개 이상 구매시 할인율(%)"
                                        id="quantityDiscount"
                                        name="quantityDiscount"
                                        value={params.quantityDiscount}
                                        onChange={(e) => {
                                            let onlyNums = e.target.value.replace(/[^0-9]/g, '')
                                            if (Number(onlyNums) > 999999999) { onlyNums = '999999999' }
                                            handleUpdateField({ target: { name: 'quantityDiscount', value: onlyNums } })
                                        }}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={4} sm={4} md={4}>
                                <FormControl fullWidth>
                                    <TextField
                                        InputProps={{ readOnly: !editMode || saving }}
                                        disabled={saving}
                                        required
                                        type="text"
                                        label="금형 금액 (단위: 원)"
                                        id="moldPrice"
                                        name="moldPrice"
                                        value={params.moldPrice}
                                        onChange={(e) => {
                                            let onlyNums = e.target.value.replace(/[^0-9]/g, '')
                                            if (Number(onlyNums) > 999999999) { onlyNums = '999999999' }
                                            handleUpdateField({ target: { name: 'moldPrice', value: onlyNums } })
                                        }}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={4} sm={4} md={4}>
                                <FormControl fullWidth>
                                    <TextField
                                        InputProps={{ readOnly: !editMode || saving }}
                                        disabled={saving}
                                        required
                                        type="text"
                                        label="디자인 금액 (단위: 원)"
                                        id="designPrice"
                                        name="designPrice"
                                        value={params.designPrice}
                                        onChange={(e) => {
                                            let onlyNums = e.target.value.replace(/[^0-9]/g, '')
                                            if (Number(onlyNums) > 999999999) { onlyNums = '999999999' }
                                            handleUpdateField({ target: { name: 'designPrice', value: onlyNums } })
                                        }}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={4} sm={4} md={4}>
                                <FormControl fullWidth>
                                    <TextField
                                        InputProps={{ readOnly: !editMode || saving }}
                                        disabled={saving}
                                        required
                                        type="text"
                                        label="개별 포장 금액 (단위: 원)"
                                        id="packingPrice"
                                        name="packingPrice"
                                        value={params.packingPrice}
                                        onChange={(e) => {
                                            let onlyNums = e.target.value.replace(/[^0-9]/g, '')
                                            if (Number(onlyNums) > 999999999) { onlyNums = '999999999' }
                                            handleUpdateField({ target: { name: 'packingPrice', value: onlyNums } })
                                        }}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        InputProps={{ readOnly: !editMode || saving }}
                                        disabled={saving}
                                        label="원산지"
                                        required
                                        id="info1"
                                        name="info1"
                                        value={params.info1}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        InputProps={{ readOnly: !editMode || saving }}
                                        disabled={saving}
                                        label="소재"
                                        required
                                        id="info2"
                                        name="info2"
                                        value={params.info2}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        InputProps={{ readOnly: !editMode || saving }}
                                        disabled={saving}
                                        label="색상"
                                        required
                                        id="info3"
                                        name="info3"
                                        value={params.info3}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        InputProps={{ readOnly: !editMode || saving }}
                                        disabled={saving}
                                        label="재고 상태"
                                        required
                                        id="info4"
                                        name="info4"
                                        value={params.info4}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        InputProps={{ readOnly: !editMode || saving }}
                                        disabled={saving}
                                        label="제작 기간"
                                        required
                                        id="info5"
                                        name="info5"
                                        value={params.info5}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" sx={{ gap: 1.5, mt: 3 }}>
                            {!editMode && (
                                <Button
                                    type="button"
                                    variant="contained"
                                    color="primary"
                                    sx={{ ...FORM_INLINE_ACTION_BUTTON_SX, minWidth: 88 }}
                                    onClick={handleEditClick}
                                    disabled={saving}
                                >
                                    수정
                                </Button>
                            )}
                            {editMode && (
                                <>
                                    <Button
                                        type="button"
                                        variant="contained"
                                        color="secondary"
                                        sx={{ ...FORM_INLINE_ACTION_BUTTON_SX, minWidth: 88 }}
                                        onClick={handleCancelClick}
                                        disabled={saving}
                                    >
                                        취소
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        sx={{ ...FORM_INLINE_ACTION_BUTTON_SX, minWidth: 98 }}
                                        disabled={saving}
                                        startIcon={saving ? BUTTON_LOADING_ICON : undefined}
                                        aria-busy={saving}
                                    >
                                        {saving ? '저장 중...' : '저장'}
                                    </Button>
                                </>
                            )}
                        </Box>
                        
                    </Box>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
